import React, { useState, useRef } from 'react';
import imageCompression from 'browser-image-compression';
import { useTranslation } from 'react-i18next';

const FileUploader = ({ 
    accept = ".png,.jpg,.jpeg,.webp", 
    limit = 1, 
    onFilesSelected,
    label
}) => {
    const { t } = useTranslation();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isCompressing, setIsCompressing] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        if (selectedFiles.length + files.length > limit) {
            alert(t('upload_limit_error', { limit }));
            return;
        }

        setIsCompressing(true);
        const newFiles = [];

        for (const file of files) {
            try {
                const options = {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true
                };
                const compressedFile = await imageCompression(file, options);
                
                // Create preview URL
                const previewUrl = URL.createObjectURL(compressedFile);
                
                newFiles.push({
                    file: compressedFile,
                    preview: previewUrl,
                    name: file.name
                });
            } catch (error) {
                console.error("Error compressing file:", error);
                alert(t('compression_error', { fileName: file.name }));
            }
        }

        const updatedFiles = [...selectedFiles, ...newFiles];
        setSelectedFiles(updatedFiles);
        
        if (onFilesSelected) {
            onFilesSelected(updatedFiles.map(f => f.file));
        }
        
        setIsCompressing(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const removeFile = (index) => {
        const updatedFiles = selectedFiles.filter((_, i) => i !== index);
        setSelectedFiles(updatedFiles);
        if (onFilesSelected) {
            onFilesSelected(updatedFiles.map(f => f.file));
        }
        URL.revokeObjectURL(selectedFiles[index].preview);
    };

    return (
        <div className="w-full mb-[20px]">
            <label className='text-[14px] text-gray-700 font-bold mb-[10px] block'>{label || t('upload_image')}</label>
            
            <div className="flex flex-col gap-4">
                {selectedFiles.length < limit && (
                    <div 
                        onClick={() => fileInputRef.current.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-taction transition-colors bg-gray-50 h-[150px]"
                    >
                        <input 
                            type="file" 
                            accept={accept} 
                            multiple={limit > 1} 
                            onChange={handleFileChange} 
                            className="hidden" 
                            ref={fileInputRef}
                        />
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400 mb-2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <p className="text-sm text-gray-500 text-center">
                            {isCompressing ? t('compressing') : t('click_to_upload')}
                        </p>
                        <p className="text-xs text-gray-400 mt-1 text-center">
                            {t('supported_formats', { formats: accept.replace(/\./g, ' ').toUpperCase() })}
                        </p>
                    </div>
                )}

                {selectedFiles.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {selectedFiles.map((fileObj, index) => (
                            <div key={index} className="relative group border rounded-lg overflow-hidden h-[100px] w-[100px]">
                                <img 
                                    src={fileObj.preview} 
                                    alt={fileObj.name} 
                                    className="w-full h-full object-cover" 
                                />
                                <button
                                    onClick={() => removeFile(index)}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    type="button"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FileUploader;